import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../api/client";
import ScoreBadge from "../components/ScoreBadge";
import LoadingSpinner from "../components/LoadingSpinner";

function StoresList() {
    const navigate = useNavigate();
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("issues");
    const [viewFilter, setViewFilter] = useState("onboarded"); // "onboarded" | "cams" | "all"
    const [stateFilter, setStateFilter] = useState("all");

    useEffect(() => {
        api.stores()
            .then((data) => {
                setStores(data);
                setError(null);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <LoadingSpinner message="Loading stores..." />;

    if (error) {
        return (
            <div className="empty-state">
                <h3>Failed to load stores</h3>
                <p>{error}</p>
                <button className="retry-btn" onClick={() => window.location.reload()}>Retry</button>
            </div>
        );
    }

    const states = [...new Set(stores.map((s) => s.state).filter(Boolean))].sort();
    const onboardedStores = stores.filter((s) => s.isOnboarded);
    const camStores = stores.filter((s) => s.nvrCameras > 0);

    const baseList = viewFilter === "onboarded" ? onboardedStores
        : viewFilter === "cams" ? camStores
        : stores;

    const filtered = baseList
        .filter((s) => {
            if (stateFilter !== "all" && s.state !== stateFilter) return false;
            if (!search) return true;
            const q = search.toLowerCase();
            return (
                (s.name || "").toLowerCase().includes(q) ||
                String(s.siteId).includes(q) ||
                (s.state || "").toLowerCase().includes(q)
            );
        })
        .sort((a, b) => {
            if (sortBy === "score-asc") return (a.latestScore || 0) - (b.latestScore || 0);
            if (sortBy === "score-desc") return (b.latestScore || 0) - (a.latestScore || 0);
            if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
            if (sortBy === "issues") return (b.openIssues || 0) - (a.openIssues || 0);
            if (sortBy === "recent") {
                const aTime = a.lastScanAt ? new Date(a.lastScanAt).getTime() : 0;
                const bTime = b.lastScanAt ? new Date(b.lastScanAt).getTime() : 0;
                return bTime - aTime;
            }
            return 0;
        });

    const toggleBtnStyle = (active) => ({
        padding: "6px 16px",
        borderRadius: 6,
        border: "1px solid " + (active ? "var(--primary)" : "var(--border)"),
        background: active ? "var(--primary)" : "var(--bg-hover)",
        color: active ? "#fff" : "var(--text-secondary)",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: active ? 600 : 400,
    });

    return (
        <div>
            <div className="page-header">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                        <h2>Stores</h2>
                        <p>{filtered.length} stores shown</p>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                        <button
                            style={toggleBtnStyle(viewFilter === "onboarded")}
                            onClick={() => setViewFilter("onboarded")}
                        >
                            Onboarded ({onboardedStores.length})
                        </button>
                        <button
                            style={toggleBtnStyle(viewFilter === "cams")}
                            onClick={() => setViewFilter("cams")}
                        >
                            Have Cams ({camStores.length})
                        </button>
                        <button
                            style={toggleBtnStyle(viewFilter === "all")}
                            onClick={() => setViewFilter("all")}
                        >
                            All ({stores.length})
                        </button>
                    </div>
                </div>
            </div>

            <div className="filter-row" style={{ marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                    type="text"
                    className="filter-select"
                    placeholder="Search by name, site ID, or state..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ minWidth: 260 }}
                />
                <select
                    className="filter-select"
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                >
                    <option value="all">All States</option>
                    {states.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
                <select
                    className="filter-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                >
                    <option value="issues">Most Issues</option>
                    <option value="recent">Most Recent Scan</option>
                    <option value="score-asc">Score (worst first)</option>
                    <option value="score-desc">Score (best first)</option>
                    <option value="name">Name</option>
                </select>
            </div>

            <div className="card">
                {filtered.length > 0 ? (
                    <table className="store-table">
                        <thead>
                            <tr>
                                <th>Site ID</th>
                                <th>Name</th>
                                <th>State</th>
                                <th>NVR Cams</th>
                                <th>PTZ Presets</th>
                                <th>Score</th>
                                <th>Open Issues</th>
                                <th>Last Scan</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((store) => (
                                <tr
                                    key={store.siteId}
                                    onClick={() => navigate(`/store/${store.siteId}`)}
                                    style={{ opacity: store.isOnboarded || store.nvrCameras > 0 ? 1 : 0.5 }}
                                >
                                    <td style={{ color: "var(--text-muted)", fontSize: 13 }}>
                                        {store.siteId}
                                    </td>
                                    <td>
                                        <div className="store-name" style={{ color: "#22c55e" }}>{store.name}</div>
                                        {store.suburb && (
                                            <div className="store-id">{store.suburb}</div>
                                        )}
                                    </td>
                                    <td>{store.state}</td>
                                    <td style={{ textAlign: "center" }}>
                                        {store.nvrCameras != null ? (
                                            <span style={{ color: store.nvrCameras > 0 ? "var(--text-primary)" : "var(--text-muted)" }}>
                                                {store.nvrCameras}
                                            </span>
                                        ) : (
                                            <span style={{ color: "var(--text-muted)" }}>{"\u2014"}</span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                        {store.ptzPresets > 0 ? (
                                            <span style={{ color: "#6366f1", fontWeight: 600 }}>{store.ptzPresets}</span>
                                        ) : (
                                            <span style={{ color: "var(--text-muted)" }}>0</span>
                                        )}
                                    </td>
                                    <td><ScoreBadge score={store.latestScore} /></td>
                                    <td>
                                        {store.openIssues > 0 ? (
                                            <span style={{ color: "var(--orange)" }}>{store.openIssues}</span>
                                        ) : (
                                            <span style={{ color: "var(--text-muted)" }}>0</span>
                                        )}
                                    </td>
                                    <td style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                                        {store.lastScanAt
                                            ? new Date(store.lastScanAt).toLocaleString()
                                            : "\u2014"}
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <div style={{ display: "flex", gap: 4 }}>
                                            {[
                                                { label: "Reports", path: "reports" },
                                                { label: "Issues", path: "issues" },
                                                { label: "Images", path: "hourly" },
                                                { label: "Details", path: "" },
                                            ].map((btn) => (
                                                <Link
                                                    key={btn.path}
                                                    to={`/store/${store.siteId}${btn.path ? '/' + btn.path : ''}`}
                                                    style={{
                                                        fontSize: 11,
                                                        padding: "2px 7px",
                                                        borderRadius: 4,
                                                        border: "1px solid var(--border)",
                                                        color: "var(--primary)",
                                                        textDecoration: "none",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {btn.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="empty-state">
                        <h3>No stores found</h3>
                        <p>Try adjusting your search criteria or filter.</p>
                    </div>
                )}
            </div>

            <div style={{ textAlign: "center", padding: 16, color: "var(--text-muted)", fontSize: 13 }}>
                Showing {filtered.length} of {baseList.length} stores
            </div>
        </div>
    );
}

export default StoresList;
